import {
    ICorporationResponse,
    ILocationResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { FileUploadButton } from 'components/button/FileUploadButton';
import { useNotificationContext } from 'contexts/NotificationContext';
import {
    FileNameTooLongError,
    FileTooLargeError,
    UnsupportedFileTypeError,
} from 'pages/Settings/FileLibrary/shared';
import { useFileUpload } from 'pages/Settings/FileLibrary/useFileUpload';
import { useRef } from 'react';
import { isFileAJpeg, normalizeImageFileRotation } from 'utils/file';
import { sentryCaptureException } from 'utils/sentry';

export type MultiFileUploadProps = Readonly<{
    /**
     * upload callback
     */
    onUpload: () => void;
    /**
     * The current corporation
     */
    corporation?: ICorporationResponse;
    /**
     * The current location
     */
    location?: ILocationResponse;
    /**
     * The groups ids used to associate groups to the file(s)
     */
    groupIds?: string[];

    /**
     * Set the upload to be multi or single file
     */
    isMultiFile?: boolean;

    /**
     * Set the button appearance style
     */
    buttonAppearance?: 'primary' | 'secondary' | 'tertiary' | 'subtle';

    /**
     * Set the button spacing; none, normal, medium, large, undefined
     */
    buttonSpacing?: 'none' | 'medium' | 'compact' | 'large' | undefined;

    /**
     * Id to set as the ParentFolderId for the uploaded file
     */
    parentFolderId?: string;

    /**
     * Whether to show the inline uploading spinner below the button.
     * Set to false when the parent handles its own loading state.
     */
    showSpinner?: boolean;
}>;

export function MultiFileUpload({
    onUpload,
    location,
    corporation,
    groupIds,
    isMultiFile = true,
    buttonAppearance,
    buttonSpacing = 'medium',
    parentFolderId,
    showSpinner = true,
}: MultiFileUploadProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { showToast } = useNotificationContext();
    const { mutate: uploadFile, isError, error, isPending } = useFileUpload(
        {
            location,
            corporation,
            groupIds,
            parentFolderId,
        },
        {
            onSuccess: (data) => {
                onUpload();
                showToast({
                    type: 'success',
                    content: `${data.DisplayName} uploaded successfully.`,
                });
            },
            onError: (e, data) => {
                const isValidationError =
                    e instanceof UnsupportedFileTypeError ||
                    e instanceof FileTooLargeError ||
                    e instanceof FileNameTooLongError;

                if (!isValidationError) {
                    sentryCaptureException({
                        error: e,
                        tags: { component: 'MultiFileUpload' },
                        context: { upload: { fileName: data.name, groupIds } },
                    });
                }

                const message =
                    e instanceof UnsupportedFileTypeError
                        ? 'Unsupported file type. Please upload a .PNG, .JPG, or .WEBP file.'
                        : e instanceof FileTooLargeError
                        ? 'File is too large. Please upload a file smaller than 10MB.'
                        : e instanceof FileNameTooLongError
                        ? 'File name is too long.'
                        : `An error occurred while uploading file: ${data.name}.`;

                showToast({ type: 'error', content: message });
            },
        }
    );

    const onUploadInputChange = () => {
        if (fileInputRef.current) {
            const { files } = fileInputRef.current;
            const filesList = Array.from(files as ArrayLike<File>);

            filesList.forEach(async (fileData) => {
                try {
                    if (isFileAJpeg(fileData)) {
                        const normalizedFile = await normalizeImageFileRotation(fileData);
                        uploadFile({ data: normalizedFile, name: fileData.name });
                    } else {
                        uploadFile({ data: fileData, name: fileData.name });
                    }
                } catch (e) {
                    sentryCaptureException({
                        error: e instanceof Error ? e : new Error(String(e)),
                        tags: { component: 'MultiFileUpload' },
                        context: {
                            file: {
                                fileName: fileData.name,
                                fileSize: fileData.size,
                                fileType: fileData.type,
                            },
                        },
                    });
                    showToast({
                        type: 'error',
                        content: `Failed to process file: ${fileData.name}. The file may be corrupted.`,
                    });
                }
            });

            // Clear the file input so that the onchange will
            // trigger if the same file is selected again
            fileInputRef.current.value = '';
        }
    };

    return (
        <FileUploadButton
            error={error}
            isError={isError}
            fileInputRef={fileInputRef}
            isMultiFile={isMultiFile}
            buttonAppearance={buttonAppearance}
            buttonSpacing={buttonSpacing}
            onUpload={onUploadInputChange}
            onButtonClick={() => fileInputRef.current?.click()}
            isUploading={isPending}
            buttonLabel={`Upload ${isMultiFile ? 'Files' : 'File'}`}
            absolutePositionedSpinner
            showSpinner={showSpinner}
        />
    );
}
